import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';

import { useAuth } from '../hooks/useAuth';
import { useMotion } from '../hooks/useMotion';
import { api } from '../lib/api';
import { dayShape } from '../lib/dayShape';
import { dayPart, greetingSlot, greetingTitle, shouldGreet, type DayPart } from '../lib/greeting';
import { formatDuration } from '../lib/time';

const SLOT_KEY = 'whoworks:lastGreeting';

// The official lockup: the ONVI mark with the clock, plus WORKS. Cut out of
// brand/onvi-works.png by scripts/brand-assets.py, so it drops onto the light
// and the dark theme alike.
export const BRAND_LOGO = '/onvi-works-logo.png';
// The mark on its own — the O with the clock. For places too small for the
// full lockup: the assistant header, the sidebar entry.
export const BRAND_MARK = '/onvi-mark.png';
const AUTO_DISMISS_MS = 4200;

const localBounds = (offsetDays: number): { from: string; to: string } => {
  const day = new Date();
  day.setDate(day.getDate() + offsetDays);
  const start = new Date(day.getFullYear(), day.getMonth(), day.getDate(), 0, 0, 0, 0);
  const end = new Date(day.getFullYear(), day.getMonth(), day.getDate(), 23, 59, 59, 999);
  return { from: start.toISOString(), to: end.toISOString() };
};

const hm = (iso: string): string =>
  new Date(iso).toLocaleTimeString('sk-SK', { hour: '2-digit', minute: '2-digit' });

const longDate = (): string =>
  new Date().toLocaleDateString('sk-SK', { weekday: 'long', day: 'numeric', month: 'long' });

type Line = { label: string; value: string };

/**
 * The app's front door. On the first open of a part of the day it says hello
 * and shows the shape of that day, then settles into Domov on its own. It
 * renders ON TOP of the dashboard, which keeps loading underneath — a greeting
 * that delays data would be a worse app, not a nicer one.
 */
export const GreetingOverlay = () => {
  const { user, streakDays } = useAuth();
  const { greeting: mode, animates } = useMotion();

  // Decided once per mount: a greeting that appears mid-session would be a
  // pop-up, not a welcome.
  const [slot] = useState<string | null>(() => {
    if (!mode || mode === 'off') {
      return null;
    }
    const now = new Date();
    const stored = localStorage.getItem(SLOT_KEY);
    return shouldGreet(mode, now, stored) ? greetingSlot(now, mode) : null;
  });
  const [open, setOpen] = useState(slot !== null);
  const part: DayPart = useMemo(() => dayPart(new Date()), []);

  useEffect(() => {
    if (!open || !slot) {
      return;
    }
    localStorage.setItem(SLOT_KEY, slot);
    const timer = window.setTimeout(() => setOpen(false), AUTO_DISMISS_MS);
    return () => window.clearTimeout(timer);
  }, [open, slot]);

  const enabled = open && Boolean(user);

  const projectsQuery = useQuery({
    queryKey: ['projects', 'list', false],
    queryFn: () => api.projectsList(false),
    enabled,
    staleTime: 15_000,
  });
  const todayQuery = useQuery({
    queryKey: ['sessions', 'greeting', 'today'],
    queryFn: () => api.sessionsList({ ...localBounds(0), userId: user?.id, limit: 100 }),
    enabled,
    staleTime: 30_000,
  });
  const plansQuery = useQuery({
    queryKey: ['plans', 'greeting', part === 'evening' ? 'tomorrow' : 'today'],
    queryFn: () => api.plansList({ ...localBounds(part === 'evening' ? 1 : 0), userId: user?.id }),
    enabled,
    staleTime: 60_000,
  });
  const tasksQuery = useQuery({
    queryKey: ['tasks', 'greeting', user?.id ?? 0],
    queryFn: () => api.tasksList({ userId: user?.id, includeCompleted: 0 }),
    enabled,
    staleTime: 60_000,
  });

  const lines = useMemo<Line[]>(() => {
    const projects = projectsQuery.data?.items ?? [];
    const colorOf = (projectId: number | null) =>
      projects.find((project) => project.id === projectId)?.color ?? '#8e8e93';
    const today = dayShape(todayQuery.data?.items ?? [], colorOf);
    const plan = (plansQuery.data?.items ?? [])[0] ?? null;
    const openTasks = (tasksQuery.data?.items ?? []).filter((task) => !task.archivedAt);
    const working = projects
      .flatMap((project) =>
        project.workers
          .filter((worker) => worker.userId !== user?.id)
          .map((worker) => `${worker.userName} · ${project.name}`),
      )
      .slice(0, 2);

    const planLine = plan
      ? `${hm(plan.startTime)} – ${hm(plan.endTime)} · ${plan.projectName}`
      : 'nič naplánované';

    if (part === 'morning') {
      return [
        { label: 'Plán na dnes', value: planLine },
        { label: 'Už pracuje', value: working.length > 0 ? working.join(', ') : 'zatiaľ nikto' },
        { label: 'Čaká na teba', value: `${openTasks.length} ticketov` },
        ...(streakDays > 1 ? [{ label: 'Séria', value: `${streakDays} dní po sebe` }] : []),
      ];
    }
    if (part === 'afternoon') {
      return [
        { label: 'Dnes natrackované', value: formatDuration(today.totalSeconds) },
        { label: 'Z plánu', value: planLine },
        { label: 'Práve pracuje', value: working.length > 0 ? working.join(', ') : 'nikto' },
      ];
    }
    return [
      { label: 'Dnes spolu', value: formatDuration(today.totalSeconds) },
      { label: 'Ostalo otvorené', value: `${openTasks.length} ticketov` },
      { label: 'Zajtra', value: planLine },
    ];
  }, [
    part,
    projectsQuery.data?.items,
    todayQuery.data?.items,
    plansQuery.data?.items,
    tasksQuery.data?.items,
    streakDays,
    user?.id,
  ]);

  if (!open || !user) {
    return null;
  }

  // Composes, never replaces: spreading a bare className over an element that
  // already had one silently strips its styling.
  const rise = (base: string, delay: number) =>
    animates
      ? { className: `${base} greet-up`, style: { animationDelay: `${delay}ms` } }
      : { className: base };

  return (
    <div className="greet-overlay" role="presentation" onPointerDown={() => setOpen(false)}>
      {/* Brand light behind everything — the only colour on the screen, so the
          mark reads as the source of it. */}
      <div className="greet-aura" aria-hidden="true" />

      <div className="greet-stage">
        <img
          src={BRAND_MARK}
          alt=""
          aria-hidden="true"
          className={`greet-mark ${animates ? 'greet-mark-in' : ''}`}
        />

        <p {...rise('greet-date', 200)}>{longDate()}</p>
        <h2 {...rise('greet-title', 280)}>{greetingTitle(part, user.name)}</h2>

        <dl className="greet-lines">
          {lines.map((line, index) => (
            <div key={line.label} {...rise('greet-line', 400 + index * 90)}>
              <dt>{line.label}</dt>
              <dd>{line.value}</dd>
            </div>
          ))}
        </dl>
      </div>

      {/* Set as text, not as the lockup: at this size the artwork turned into an
          unreadable smudge, and the name is what anchors the bottom anyway. */}
      <p className="greet-brand" aria-hidden="true">
        ONVI WORKS
      </p>

      {/* The screen leaves on its own; the bar is what says so before it does. */}
      {animates ? (
        <span
          className="greet-progress"
          style={{ animationDuration: `${AUTO_DISMISS_MS}ms` }}
          aria-hidden="true"
        />
      ) : null}
    </div>
  );
};
